import { useEffect, useRef } from 'react';

export function AccentRain() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let animId: number;
    let lastTime = 0;
    const targetFPS = 8;
    const frameInterval = 1000 / targetFPS;
    const fontSize = 14;
    const katakana = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
    const digits = '0123456789';
    const allChars = katakana + digits;

    function resize() {
      const parent = canvas!.parentElement;
      if (!parent) return;
      canvas!.width = parent.clientWidth;
      canvas!.height = parent.clientHeight;
    }
    resize();

    const totalCols = Math.floor(canvas.width / fontSize);
    const activeCols = Math.max(1, Math.floor(totalCols * 0.08));
    const colIndices: number[] = [];
    for (let c = 0; c < activeCols; c += 1) {
      colIndices.push(totalCols - 1 - c * 3);
    }

    const drops: Map<number, number> = new Map();
    const speeds: Map<number, number> = new Map();
    for (const ci of colIndices) {
      drops.set(ci, Math.random() * -40);
      speeds.set(ci, 0.08 + Math.random() * 0.12);
    }

    function draw(timestamp: number) {
      animId = requestAnimationFrame(draw);
      const delta = timestamp - lastTime;
      if (delta < frameInterval) return;
      lastTime = timestamp - (delta % frameInterval);

      ctx!.fillStyle = 'rgba(8, 8, 9, 0.05)';
      ctx!.fillRect(0, 0, canvas!.width, canvas!.height);
      ctx!.font = `${fontSize}px monospace`;

      for (const ci of colIndices) {
        const x = ci * fontSize;
        const drop = drops.get(ci) ?? 0;
        const speed = speeds.get(ci) ?? 0.1;
        const y = drop * fontSize;

        const char = allChars[Math.floor(Math.random() * allChars.length)];
        ctx!.shadowColor = '#2a6e0a';
        ctx!.shadowBlur = 3;
        ctx!.fillStyle = 'rgba(147, 255, 24, 0.6)';
        ctx!.fillText(char!, x, y);
        ctx!.shadowBlur = 0;

        for (let t = 1; t < 10; t += 1) {
          const trailY = y - t * fontSize;
          if (trailY < 0) break;
          if (Math.random() <= 0.4) {
            const tc = allChars[Math.floor(Math.random() * allChars.length)];
            const alpha = Math.max(0, 1 - t / 10);
            ctx!.fillStyle = `rgba(10, ${Math.floor(80 + 80 * alpha)}, 8, ${alpha * 0.3})`;
            ctx!.fillText(tc!, x, trailY);
          }
        }

        drops.set(ci, drop + speed);
        if (drop * fontSize > canvas!.height && Math.random() > 0.96) {
          drops.set(ci, Math.random() * -20);
          speeds.set(ci, 0.08 + Math.random() * 0.12);
        }
      }
    }

    animId = requestAnimationFrame(draw);
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className='pointer-events-none absolute inset-0 size-full' style={{ opacity: 0.35 }} aria-hidden='true' />;
}
